import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, ArrowRight } from 'lucide-react';
import { usePosts } from '../hooks/usePosts';
import NewsletterSection from '../components/NewsletterSection';

const BlogPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { posts, pagination, loading, error } = usePosts(currentPage, 12);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleNextPage = () => {
    if (pagination && currentPage < pagination.pages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      <section style={{ paddingTop: '120px', paddingBottom: '4rem', background: 'var(--off-white)' }}>
        <div className="container">
          <div className="section-header" style={{ marginBottom: '2rem' }}>
            <span className="section-tag">News & Stories</span>
            <h1>ROLF Blog</h1>
            <p>Updates, stories, and insights from our work in the community.</p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)' }}>Loading posts...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div style={{
              background: '#fee8e8',
              border: '1px solid #d96f7b',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              marginBottom: '2rem',
              color: '#c42a45'
            }}>
              <p style={{ margin: 0 }}>Error loading posts: {error}</p>
            </div>
          )}

          {/* Posts Grid */}
          {!loading && posts.length > 0 && (
            <>
              <div className="grid grid-3">
                {posts.map((post, i) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="scroll-animate blog-card" style={{ transitionDelay: `${i * 100}ms`, height: '100%', cursor: 'pointer', transition: 'transform 0.3s ease' }}>
                      <div style={{
                        height: '180px',
                        background: `linear-gradient(135deg, ${post.category?.color || 'var(--navy)'} 0%, var(--teal) 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <BookOpen size={40} style={{ color: 'rgba(255,255,255,0.3)' }} />
                      </div>
                      <div className="blog-card-body">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                          <span className="blog-card-date">
                            <Calendar size={12} style={{ display: 'inline', marginRight: '0.25rem' }} />
                            {new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </span>
                          <span
                            className="programme-card-category"
                            style={{
                              background: post.category?.color || 'var(--navy)',
                              color: 'white'
                            }}
                          >
                            {post.category?.name}
                          </span>
                        </div>
                        <h4 className="blog-card-title">{post.title}</h4>
                        <p className="blog-card-excerpt">{post.excerpt}</p>
                        <button style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          marginTop: '1rem',
                          color: 'var(--teal)',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer'
                        }}>
                          Read more <ArrowRight size={14} />
                        </button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.pages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1rem',
                  marginTop: '3rem',
                  paddingTop: '2rem',
                  borderTop: '1px solid var(--border-color)'
                }}>
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: currentPage === 1 ? '#e0e0e0' : 'var(--teal)',
                      color: currentPage === 1 ? '#999' : 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    ← Previous
                  </button>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'var(--text-secondary)'
                  }}>
                    Page {currentPage} of {pagination.pages}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === pagination.pages}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: currentPage === pagination.pages ? '#e0e0e0' : 'var(--teal)',
                      color: currentPage === pagination.pages ? '#999' : 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: currentPage === pagination.pages ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}

          {/* No Posts State */}
          {!loading && posts.length === 0 && !error && (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)' }}>No posts found.</p>
            </div>
          )}
        </div>
      </section>

      <NewsletterSection />
    </>
  );
};

export default BlogPage;
